import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateExploitRule } from '../../api/exploit-rules';
import type { ExploitBlockRuleWithStatus } from '../../types/exploit-rules';

interface PatternEditModalProps {
  rule: ExploitBlockRuleWithStatus;
  onClose: () => void;
}

export function PatternEditModal({ rule, onClose }: PatternEditModalProps) {
  const { t } = useTranslation('exploitRules');
  const queryClient = useQueryClient();
  const [pattern, setPattern] = useState(rule.pattern || '');
  const [patternType, setPatternType] = useState(rule.pattern_type || 'query_string');
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState<'matched' | 'not_matched' | 'invalid' | null>(null);

  const updateMutation = useMutation({
    mutationFn: () => updateExploitRule(rule.id, { pattern, pattern_type: patternType }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exploit-rules'] });
      queryClient.invalidateQueries({ queryKey: ['host-exploit-rules'] });
      onClose();
    },
  });

  const testPattern = useCallback(() => {
    if (!testInput.trim()) {
      setTestResult(null);
      return;
    }
    try {
      const regex = new RegExp(pattern, 'i');
      setTestResult(regex.test(testInput) ? 'matched' : 'not_matched');
    } catch {
      setTestResult('invalid');
    }
  }, [pattern, testInput]);

  useEffect(() => {
    testPattern();
  }, [testPattern]);

  const hasChanges = pattern !== rule.pattern || patternType !== rule.pattern_type;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b dark:border-slate-700 bg-amber-50 dark:bg-amber-900/20 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('patternEdit.title')}
              </h3>
              <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                {rule.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Pattern Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              {t('patternEdit.patternType')}
            </label>
            <select
              value={patternType}
              onChange={(e) => setPatternType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
            >
              <option value="query_string">{t('patternEdit.types.query_string')}</option>
              <option value="request_uri">{t('patternEdit.types.request_uri')}</option>
              <option value="user_agent">{t('patternEdit.types.user_agent')}</option>
              <option value="request_method">{t('patternEdit.types.request_method')}</option>
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
              {t('patternEdit.patternTypeHelp')}
            </p>
          </div>

          {/* Pattern */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              {t('patternEdit.pattern')}
            </label>
            <textarea
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-slate-700 text-gray-900 dark:text-white resize-none"
              rows={3}
              spellCheck={false}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
              {t('patternEdit.patternHelp')}
            </p>
          </div>

          {/* Test Pattern */}
          <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
              {t('patternEdit.testPattern')}
            </h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 dark:text-slate-400 mb-1">
                  {t('patternEdit.testInput')}
                </label>
                <input
                  type="text"
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  placeholder={t('patternEdit.testInputPlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                />
              </div>
              {testResult && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                  testResult === 'matched'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : testResult === 'not_matched'
                    ? 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                }`}>
                  {testResult === 'matched' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {testResult === 'not_matched' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                  {testResult === 'invalid' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                  <span>
                    {testResult === 'matched' && t('patternEdit.testMatched')}
                    {testResult === 'not_matched' && t('patternEdit.testNotMatched')}
                    {testResult === 'invalid' && t('patternEdit.testInvalid')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Error/Success Messages */}
          {updateMutation.isError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
              {t('patternEdit.saveError')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex items-center justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
          >
            {t('patternEdit.cancel')}
          </button>
          <button
            onClick={() => updateMutation.mutate()}
            disabled={!hasChanges || updateMutation.isPending || testResult === 'invalid'}
            className={`px-4 py-2 text-sm rounded-lg transition-colors ${
              hasChanges && testResult !== 'invalid'
                ? 'bg-amber-600 text-white hover:bg-amber-700 dark:hover:bg-amber-500'
                : 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
            }`}
          >
            {updateMutation.isPending ? t('patternEdit.saving') : t('patternEdit.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
